// Single row in the unified Section 2
// (Additional Certifications) surface. Visually + behaviorally identical
// to a Section 1 <CriteriaChecklistItemRow>: a status chip, three action
// buttons (CERTIFY / DECLINE / ESCALATE), and an OPEN FORM modal.
//
// This is a clone of the Section 1 row rather than a direct reuse:
// <CriteriaChecklistItemRow> is hard-coupled to CriteriaChecklistItemDto,
// the catalog / formSchema system and lane permissions, none of which apply
// to an UnderwritingCertification. The visible structure (badges, button
// variants/labels, validation gates) is kept pixel-identical so the two
// sections read the same. On an analyst tab a Certified row changes only
// through Admin Undo (owner ruling 15, 2026-10-08), offered beside its chip;
// Closing Step 1 has the same rules since 2026-10-13, and only B2 or ADMIN may
// act on it. An Escalated row offers Certify and Decline unless its latest
// escalation is still Pending (the server's rule).

import { useState } from "react";
import { CheckCircle2, FileText, MoreVertical } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  canCertifyChecklistItem,
  canDeclineChecklistItem,
  canEscalateChecklistItem,
} from "@/lib/dealFlowPermissions";
import {
  useCertifyCertification,
  useDeclineCertification,
  useEscalateCertification,
} from "@/hooks/dealFlow/useDealFlow";
import {
  useUndoAnalystS2Certification,
  useUndoClosingS1Certification,
} from "@/hooks/dealFlow/useAdminUndo";
import { AdminUndoButton } from "../admin/AdminUndoButton";
import type {
  CriteriaItemAssessment,
  DealFlowTab,
  UnderwritingCertificationDto,
  UnderwritingCertificationStatus,
  UnderwritingCertificationTemplateDto,
} from "@/api/dealFlow/types";
import { CertifyConfirmationModal } from "../CriteriaChecklist/CertifyConfirmationModal";
import { DeclineModal } from "../CriteriaChecklist/DeclineModal";
import { EscalateModal } from "../CriteriaChecklist/EscalateModal";
import { CertificationItemModal } from "./CertificationItemModal";
import { CertificationCatalogEditModal } from "./CertificationCatalogEditModal";
import type { CertConfig } from "./types";

interface CertificationItemRowProps {
  dealId: string;
  tab: DealFlowTab;
  /** Lightweight display + action shape (code / title / blurb). */
  cert: CertConfig;
  /** The DB catalog template backing this cert, when one exists (Section 2
   *  analyst certs). Drives the admin "Edit…" kebab; omitted by the Closing
   *  tab's Section 1 certs, which have no editable catalog template. */
  template?: UnderwritingCertificationTemplateDto;
  /** Fetched row for this cert, or null when no DB row exists yet
   *  (treated as Pending). */
  row: UnderwritingCertificationDto | null;
  /** Certification section. The analyst Additional Certifications are
   *  Section 2; the Closing tab Step 1 certs are Section 1. Defaults to 2
   *  so the analyst call sites need no change. */
  section?: number;
  readOnly?: boolean;
}

/** The analyst tabs, whose Section 2 certifications only Admin Undo can
 *  change once Certified. Mirrors the server's tab → lane map. */
const ANALYST_TABS: ReadonlySet<DealFlowTab> = new Set([
  "FinancialAnalysis",
  "LegalAnalysis",
  "BusinessAnalysis",
]);

/** A cert with no row, or a row whose status is "Pending", is Pending. */
function statusOf(
  row: UnderwritingCertificationDto | null,
): UnderwritingCertificationStatus {
  return row?.status ?? "Pending";
}

export function CertificationItemRow({
  dealId,
  tab,
  cert,
  template,
  row,
  section = 2,
  readOnly = false,
}: CertificationItemRowProps) {
  const toast = useToast();
  const auth = useAuth();
  const permSet = new Set(auth.user?.permissions ?? []);
  const isAdmin = auth.hasRoleCode("ADMIN");

  const [formOpen, setFormOpen] = useState(false);
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const certify = useCertifyCertification(dealId);
  const decline = useDeclineCertification(dealId);
  const escalate = useEscalateCertification(dealId);
  const undoCertified = useUndoAnalystS2Certification(dealId, row?.id ?? "");
  const undoClosingCertified = useUndoClosingS1Certification(dealId, row?.id ?? "");

  const status = statusOf(row);
  const isClosingStepOne = tab === "Closing" && section === 1;
  // Closing Step 1 admits B2 or ADMIN; the analyst tabs gate through readOnly.
  const actorAllowed = !isClosingStepOne || isAdmin || auth.hasRoleCode("B2");
  // Pending, or Escalated with no escalation still waiting on the executive.
  const canTakeTerminalAction =
    status === "Pending" ||
    (status === "Escalated" && row?.latestEscalationStatus !== "Pending");
  const isTerminal =
    status === "Certified" || status === "Declined" || status === "Escalated";

  // Severity Assessment + Report-Uploaded Attestation +
  // a non-empty justification gate the terminal Certify / Decline actions,
  // mirroring the Section-1 checklist row. The server re-enforces.
  const assessment = row?.assessment ?? null;
  const attested = row?.reportUploadedAttested ?? false;
  const justification = (row?.justification ?? "").trim();

  const missingAssessment = assessment == null;
  const missingAttestation = !attested;
  const missingJustification = justification.length === 0;

  const certifyDisabled =
    missingAssessment || missingAttestation || missingJustification;
  const certifyTooltip = certifyDisabled
    ? "Open the form and set a Severity Assessment, confirm the report is uploaded, and enter a justification before certifying."
    : undefined;
  // Decline shares the same form gates; the decline reason itself is
  // captured in the Decline modal.
  const declineDisabled =
    missingAssessment || missingAttestation || missingJustification;
  const declineTooltip = declineDisabled
    ? "Open the form and set a Severity Assessment, confirm the report is uploaded, and enter a justification before declining."
    : undefined;
  const escalateDisabled = missingAssessment;
  const escalateTooltip = escalateDisabled
    ? "Open the form and set a Severity Assessment before escalating."
    : undefined;

  const surface = (verb: string) => (err: unknown) => {
    const extracted = extractErrorMessage(err);
    const message =
      extracted === "Something went wrong. Please try again."
        ? `Could not ${verb}.`
        : extracted;
    toast.error(message);
  };

  const identity = {
    tab,
    section,
    certificationCode: cert.certificationCode,
    label: cert.title,
  };

  const onCertify = async () => {
    try {
      await certify.mutateAsync({
        ...identity,
        assessment,
        reportUploadedAttested: attested,
        justification,
      });
      toast.success(`${cert.title} certified.`);
      setCertifyOpen(false);
    } catch (err) {
      surface("certify certification")(err);
    }
  };

  const onDecline = async (declineReason: string) => {
    try {
      await decline.mutateAsync({
        ...identity,
        assessment,
        reportUploadedAttested: attested,
        justification,
        declineReason,
      });
      toast.success(`${cert.title} declined.`);
      setDeclineOpen(false);
    } catch (err) {
      surface("decline certification")(err);
    }
  };

  const onEscalate = async (escalationReason: string) => {
    try {
      await escalate.mutateAsync({
        ...identity,
        assessment,
        escalationReason,
      });
      toast.success(`${cert.title} escalated.`);
      setEscalateOpen(false);
    } catch (err) {
      surface("escalate certification")(err);
    }
  };

  return (
    <li
      className="border border-border-light rounded-md bg-white-pure px-space-3 py-space-2"
      data-testid={`addl-cert-${cert.certificationCode}`}
      data-status={status}
    >
      <div className="flex items-start gap-space-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-space-2 mb-space-1">
            <Badge status="draft">{cert.certificationCode}</Badge>
            <StatusBadge status={status} />
            {row && status === "Certified" && section === 2 && ANALYST_TABS.has(tab) && (
              <AdminUndoButton
                label="Certified"
                modalTitle={`Undo ${cert.certificationCode} Certified`}
                modalConsequence={
                  <>
                    This returns the certification to <strong>Pending</strong>{" "}
                    (or <strong>Escalated</strong> if its latest escalation was
                    approved). The analyst&apos;s assessment and justification
                    stay. If this lane&apos;s assessment has already been
                    issued, the server refuses the undo until that issuance is
                    undone first.
                  </>
                }
                successToast={`${cert.certificationCode} reset from Certified.`}
                invoke={(reason) => undoCertified.mutateAsync(reason)}
              />
            )}
            {row && status === "Certified" && isClosingStepOne && (
              <AdminUndoButton
                label="Certified"
                modalTitle={`Undo ${cert.certificationCode} Certified`}
                modalConsequence={
                  <>
                    This returns the certification to <strong>Pending</strong>{" "}
                    (or <strong>Escalated</strong> if its latest escalation was
                    approved). The assessment and justification stay. Once the
                    loan is created, the server refuses the undo.
                  </>
                }
                successToast={`${cert.certificationCode} reset from Certified.`}
                invoke={(reason) => undoClosingCertified.mutateAsync(reason)}
              />
            )}
            {assessment && (
              <AssessmentBadge assessment={assessment} attested={attested} />
            )}
          </div>
          <div
            className="text-small text-onyx font-medium truncate"
            title={cert.title}
          >
            {cert.title}
          </div>
          <p className="mt-space-1 text-caption text-gray-mid m-0">
            {cert.description}
          </p>
        </div>
        <div className="flex items-center gap-space-1 shrink-0">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setFormOpen(true)}
            title={isTerminal ? "View form (read-only)" : "Open form"}
          >
            <FileText size={14} aria-hidden />
            {isTerminal ? "View form" : "Open form"}
          </Button>
          {!readOnly && actorAllowed && canTakeTerminalAction && canCertifyChecklistItem(permSet) && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCertifyOpen(true)}
              disabled={certifyDisabled}
              title={certifyTooltip}
            >
              Certify
            </Button>
          )}
          {!readOnly && actorAllowed && canTakeTerminalAction && canDeclineChecklistItem(permSet) && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeclineOpen(true)}
              disabled={declineDisabled}
              title={declineTooltip}
            >
              Decline
            </Button>
          )}
          {!readOnly &&
            actorAllowed &&
            status === "Pending" &&
            canEscalateChecklistItem(permSet) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEscalateOpen(true)}
                disabled={escalateDisabled}
                title={escalateTooltip}
              >
                Escalate
              </Button>
            )}
          {!readOnly && isAdmin && template && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setOverflowOpen((o) => !o)}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border-light hover:bg-white-warm"
                aria-haspopup="menu"
                aria-expanded={overflowOpen}
                aria-label="More actions"
              >
                <MoreVertical size={14} aria-hidden />
              </button>
              {overflowOpen && (
                <div
                  className="absolute right-0 top-9 z-30 min-w-[180px] bg-white-pure border border-border-light rounded-md shadow-md py-space-1"
                  role="menu"
                  onMouseLeave={() => setOverflowOpen(false)}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-space-3 py-space-1 text-small text-onyx hover:bg-white-warm"
                    onClick={() => {
                      setOverflowOpen(false);
                      setEditOpen(true);
                    }}
                  >
                    <FileText size={12} className="inline mr-space-1" aria-hidden />
                    Edit…
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {status === "Declined" && row?.declineReason && (
        <p className="mt-space-2 text-caption text-status-error m-0">
          Decline reason: {row.declineReason}
        </p>
      )}
      {status === "Escalated" && row?.escalationReason && (
        <p className="mt-space-2 text-caption text-gray-mid italic m-0">
          Escalation reason: {row.escalationReason}
        </p>
      )}

      {formOpen && (
        <CertificationItemModal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          dealId={dealId}
          tab={tab}
          section={section}
          cert={cert}
          row={row}
        />
      )}
      {editOpen && template && (
        <CertificationCatalogEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          template={template}
        />
      )}
      <CertifyConfirmationModal
        open={certifyOpen}
        onClose={() => setCertifyOpen(false)}
        itemCode={cert.certificationCode}
        itemTitle={cert.title}
        onConfirm={onCertify}
        pending={certify.isPending}
      />
      <DeclineModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        itemCode={cert.certificationCode}
        onConfirm={onDecline}
        pending={decline.isPending}
      />
      <EscalateModal
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        itemCode={cert.certificationCode}
        escalationPath={null}
        onConfirm={onEscalate}
        pending={escalate.isPending}
      />
    </li>
  );
}

function StatusBadge({ status }: { status: UnderwritingCertificationStatus }) {
  // Color tokens are identical to the Section-1 checklist row chips:
  // Pending → draft (gray), Certified → finalized (green),
  // Declined → error (red), Escalated → review (amber).
  switch (status) {
    case "Pending":
      return (
        <Badge status="draft" aria-label="Status: pending">
          Pending
        </Badge>
      );
    case "Certified":
      return <Badge status="finalized">Certified</Badge>;
    case "Declined":
      return <Badge status="error">Declined</Badge>;
    case "Escalated":
      return <Badge status="review">Escalated</Badge>;
  }
}

function AssessmentBadge({
  assessment,
  attested,
}: {
  assessment: CriteriaItemAssessment;
  attested: boolean;
}) {
  // Identical mapping to the Section-1 AssessmentBadge: Clean→finalized,
  // Minor→gold, Material→review, Critical→error.
  const tone = (() => {
    switch (assessment) {
      case "Clean":
        return "finalized" as const;
      case "Minor":
        return "gold" as const;
      case "Material":
        return "review" as const;
      case "Critical":
        return "error" as const;
    }
  })();
  return (
    <Badge status={tone} title={attested ? "Report uploaded" : undefined}>
      Assessment: {assessment.toUpperCase()}
      {attested && (
        <CheckCircle2
          size={10}
          className="ml-space-1"
          aria-label="Report uploaded"
        />
      )}
    </Badge>
  );
}
